import { ApiError } from '../api/client';

/** Un error de un campo del formulario: `name` es el atributo name del input. */
export interface FieldError {
  name: string;
  message: string;
}

/** Nombres de la API que en el formulario se llaman distinto. */
const API_TO_FIELD: Record<string, string> = { birthDate: 'birthDay', familyName: 'paternalSurname', phone: 'phone' };

/** Errores de validación del servidor → errores por campo. */
export function fieldErrorsFrom(error: unknown): FieldError[] {
  if (error instanceof ApiError && error.code === 'VALIDATION_ERROR' && Array.isArray(error.details?.issues)) {
    return (error.details.issues as { path?: string; message?: string }[])
      .filter((i) => i.path && i.message)
      .map((i) => ({ name: API_TO_FIELD[i.path!] ?? i.path!, message: i.message! }));
  }
  return [];
}

/** Quita las marcas de error del formulario. */
export function clearFieldErrors(form: HTMLFormElement): void {
  form.querySelectorAll('.field--error').forEach((el) => el.classList.remove('field--error'));
  form.querySelectorAll('.field__error').forEach((el) => el.remove());
  form.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.removeAttribute('aria-invalid'));
}

/**
 * A prueba de errores: marca en rojo cada campo con problema, escribe el motivo debajo,
 * baja la pantalla hasta el primero y lo deja listo para corregir. Al escribir en el campo, la marca se quita.
 */
export function showFieldErrors(form: HTMLFormElement, errors: FieldError[]): boolean {
  clearFieldErrors(form);
  let first: HTMLElement | null = null;
  for (const { name, message } of errors) {
    const item = form.elements.namedItem(name);
    const input = (item instanceof RadioNodeList ? (item[0] as HTMLElement | undefined) : (item as HTMLElement | null)) ?? null;
    if (!input) continue;
    const box = (input.closest('.field, fieldset, .consent') as HTMLElement | null) ?? input.parentElement;
    if (!box || box.classList.contains('field--error')) continue;
    box.classList.add('field--error');
    const targets = item instanceof RadioNodeList ? Array.from(item) : [input];
    targets.forEach((t) => (t as HTMLElement).setAttribute('aria-invalid', 'true'));
    const note = document.createElement('small');
    note.className = 'field__error';
    note.setAttribute('role', 'alert');
    note.textContent = message;
    box.appendChild(note);
    const clear = () => {
      box.classList.remove('field--error');
      note.remove();
      targets.forEach((t) => (t as HTMLElement).removeAttribute('aria-invalid'));
    };
    box.addEventListener('input', clear, { once: true });
    box.addEventListener('change', clear, { once: true });
    first ??= input;
  }
  if (first) {
    first.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    first.focus({ preventScroll: true });
    return true;
  }
  return false;
}
